import { MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { SimpleSelect } from '@/components/ui/simple-select';
import type { SimpleSelectOption } from '@/components/ui/simple-select';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { DESKTOP_BREAKPOINT_QUERY } from '@/config/ui.constants';
import { FILTER_LOCK_TOOLTIP } from '../../model/assetViewScope';
import AssetsTabList from './AssetsTabList';
import AssetsTabPills from './AssetsTabPills';
import RequestSupportButton from './RequestSupportButton';
import type { AssetTabId, AssetTabItem } from './assetTabs';

interface AssetsControlBarProps {
  tabs: AssetTabItem[];
  activeTab: AssetTabId;
  onSelectTab: (id: AssetTabId) => void;
  onOpenSupportRequest: () => void;
  hasPhotoset: boolean;
  photoSetOptions: SimpleSelectOption[];
  selectedPhotosetId: string | null;
  onPhotoSetChange: (value: string) => void;
  /** Per-tab selection cluster (checkbox/count, lg-only download/upload icons). */
  leadingActions?: React.ReactNode;
  /**
   * Desktop-only service-pills region (sub-lg reaches services via the
   * selection bar's Services sheet).
   */
  trailingActions?: React.ReactNode;
  /**
   * DV-2856 search field. Desktop renders it inline in the control row; sub-lg
   * it renders as a trigger until `searchExpanded`, at which point it replaces
   * the row's other contents (see below).
   */
  search?: React.ReactNode;
  /**
   * Sub-lg only: the search field has taken over the control row. The row keeps
   * its exact height and stickiness — only its contents swap — because the
   * hide/reveal animation must never change layout height mid-scroll (see the
   * collapseControlRow note).
   */
  searchExpanded?: boolean;
  /**
   * A photo is checked, so the view filters are frozen (decided 2026-09-18):
   * this row's photoset select goes inert. The search field takes the same flag
   * from the page directly, and the Show Hidden Photos kebab item takes it from
   * usePhotosActions — only the trigger and the display-only Show Image Details
   * stay live.
   */
  filtersLocked?: boolean;
  /** Kebab DropdownMenuContent children; kebab trigger renders only when set. */
  menu?: React.ReactNode;
  /**
   * When true, the kebab trigger itself is `lg:hidden` (desktop gets no
   * kebab at all). Use for tabs whose `menu` slot is entirely made of
   * sub-lg-only mirror items (e.g. Virtual Staging's StagedMenuItems) --
   * otherwise desktop renders a kebab whose popup is an empty box.
   */
  menuSubLgOnly?: boolean;
  /**
   * Sub-lg only: collapses the control row (select-all / photoset / kebab)
   * while the page scrolls DOWN, revealing it again the moment the user
   * scrolls up (QA round 4 follow-up — Steffon's call, refining the mobile
   * design's fully-fixed header). The pills row above stays pinned either
   * way; desktop ignores this entirely (both rows always pinned).
   */
  collapseControlRow?: boolean;
}

// 2026-08-19 design update (spec §1.3 correction): the single merged row from
// Unit 4 PR A's first pass un-merges into two rows. Desktop gets a tab row
// (tabs + Request Support, labeled) with its own border-b, and a control row
// below it (leading actions / service pills / photoset select / kebab).
// Sub-lg collapses the tab row into a full-width AssetsTabPills scroller with
// the control row (photoset select and kebab) below it -- there's no tab
// dropdown to share a row with anymore, so the old Row A / Row B split (and
// its `hasRowB` gating) is gone; the sub-lg control row always renders.
// Request Support sub-lg lives at the top of each tab's kebab, not in the row
// (QA round 3, 2026-08-19).
const Divider = () => (
  <span aria-hidden className="h-7 w-px shrink-0 bg-border" />
);

const AssetsControlBar = ({
  tabs,
  activeTab,
  onSelectTab,
  onOpenSupportRequest,
  hasPhotoset,
  photoSetOptions,
  selectedPhotosetId,
  onPhotoSetChange,
  leadingActions,
  trailingActions,
  search,
  searchExpanded = false,
  filtersLocked = false,
  menu,
  menuSubLgOnly = false,
  collapseControlRow = false
}: AssetsControlBarProps) => {
  const isDesktop = useMediaQuery(DESKTOP_BREAKPOINT_QUERY);

  const selectedOption = selectedPhotosetId
    ? photoSetOptions.find((o) => o.value === selectedPhotosetId) ?? null
    : null;

  const photoSetSelectControl = hasPhotoset && (
    <SimpleSelect
      options={photoSetOptions}
      placeholder="Select Photo Set"
      field="photoSet"
      value={selectedOption}
      setFieldValue={(_: string, value: unknown) =>
        onPhotoSetChange(value as string)
      }
      md=""
      controlId="photoSetSelect"
      ariaDescribedBy="photo-set-dropdown"
      name="photoSet"
      // 2026-08-19 design update: the design's sub-lg select is compact and
      // right-aligned, not flex-1 -- the count label / leading actions on the
      // left now need the room. Verify at 375px in QA.
      disabled={filtersLocked}
      // lg:min-w-[160px]: at narrow desktop widths (1024–1100px with the
      // sidebar open) the control row can't fit every control at full width,
      // so the select gives up to 90px (the trigger truncates its label)
      // before the row overflows. 160px still shows a full date label.
      className="w-[250px] max-w-full lg:min-w-[160px] max-lg:w-auto max-lg:min-w-0 max-lg:max-w-[200px]"
    />
  );

  // The photoset select is a view filter like the search: switching photosets
  // would swap the pool out from under a selection, so a selection freezes it
  // (decided 2026-09-18). The wrapper carries the dim and the explanation —
  // a disabled trigger fires no pointer events of its own, so the tooltip has
  // to hang off something that does.
  const photoSetSelect =
    photoSetSelectControl && filtersLocked ? (
      <Tooltip delayDuration={250}>
        <TooltipTrigger asChild>
          {/* This wrapper is the flex item while locked, so it carries the
              select's desktop shrink floor; the select's own max-w-full caps
              it to whatever width the wrapper is given. */}
          <div className="min-w-0 lg:min-w-[160px] cursor-not-allowed">
            {/* The trigger is genuinely `disabled` (keyboard included), and a
                disabled button dispatches no pointer events — the hover that
                opens this tooltip would die on it. pointer-events-none makes
                the select transparent to hit-testing so the hover lands on the
                wrapper above instead. The dim is the primitive's own
                disabled:opacity-50; adding one here would compound with it. */}
            <div className="pointer-events-none">{photoSetSelectControl}</div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom">{FILTER_LOCK_TOOLTIP}</TooltipContent>
      </Tooltip>
    ) : (
      photoSetSelectControl
    );

  const kebab = menu && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="More Options"
          className={cn('shrink-0', menuSubLgOnly && 'lg:hidden')}
        >
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      {/* min-w-16rem: these menus are icon + label + right-aligned check, and
          the default 8rem crowds them (design prototype, 2026-09-16). */}
      <DropdownMenuContent align="end" className="min-w-[16rem]">
        {menu}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <TooltipProvider>
      {/* shrink-0 (both branches): these rows are flex items in the page's
          scrolling column (flex-col overflow-y-auto). Without it, the column
          shrinks a bar below its content's min-content floor once an explicit
          min-height is in play, bleeding wrapped mobile content out both ends
          of the box and painting over the grid. */}
      {isDesktop ? (
        <div className="shrink-0 bg-card border-b border-border sticky top-0 z-10">
          {/* 2026-08-19 design update: the merged single row un-merges into a
              tab row (tabs + Request Support) and a control row below it.
              Desktop pins both rows permanently. */}
          <div className="flex items-center gap-4 border-b border-border pl-4 pr-6">
            <AssetsTabList tabs={tabs} activeTab={activeTab} onSelectTab={onSelectTab} />
            <div className="flex-1" />
            <RequestSupportButton variant="tab-row" onOpenSupportRequest={onOpenSupportRequest} />
          </div>
          <div className="flex items-center gap-3 min-h-[62px] w-full py-3 pl-4 pr-6">
            {leadingActions}
            {trailingActions && (
              <>
                <Divider />
                {/* No min-w-0: this wrapper must inherit the service pills'
                    floor (see AssetServicePills) so the search field and
                    photoset select shrink first and never paint over the
                    More/Services trigger. */}
                <div className="flex flex-1 items-center">{trailingActions}</div>
                <Divider />
              </>
            )}
            {!trailingActions && <div className="flex-1" />}
            {search}
            {photoSetSelect}
            {kebab}
          </div>
        </div>
      ) : (
        <>
          {/* Sub-lg: TWO independently-sticky rows, not one wrapper. The
              hide/reveal must never change layout height mid-scroll — a
              height-animated collapse reflowed the grid under the user's
              finger every frame and fought Chrome's scroll anchoring, which
              read as glitchy stutter (Steffon, QA round 4 follow-up). The
              pills row pins at top-0 (opaque, higher z); the control row
              pins right below it and slides UP BEHIND it with a
              compositor-only translate when collapseControlRow is set —
              constant geometry, so scrolling never feeds back into itself.
              top-16 = the pills row's fixed height (pt-3 + h-11 pills +
              pb-2 = 64px); keep them in sync if the pills row's metrics
              change. */}
          {/* before: bleed — on fractional device-pixel scaling, this pinned
              row and the page header above it (a different render layer,
              outside this scroll column) can round to different device
              pixels mid-scroll, opening a 1-2px crack that the scrolled
              grid paints through (QA round 5). The 4px bg-card strip above
              this row's own box fills any such crack; when the edges align
              the scroll column clips it away entirely, so nothing changes
              at integer DPR. */}
          <div className="sticky top-0 z-20 shrink-0 bg-card border-b border-border px-4 pt-3 pb-2 before:absolute before:inset-x-0 before:-top-1 before:h-1 before:bg-card">
            <AssetsTabPills tabs={tabs} activeTab={activeTab} onSelectTab={onSelectTab} />
          </div>
          {/* No Request Support control in this row: sub-lg it lives at the
              top of every tab's kebab instead (QA round 3, 2026-08-19 —
              Steffon call, matching the mobile design's kebab). Desktop
              keeps the tab-row text button above.

              Tailwind v4 gotcha: translate-* writes the `translate`
              property, which `transition-transform` does NOT animate — the
              transition list must name `translate` explicitly. `invisible`
              drops the hidden row from the tab order and a11y tree
              (visibility transitions discretely, so the slide still plays). */}
          <div
            data-testid="assets-control-row"
            className={cn(
              'sticky top-16 z-10 shrink-0 flex items-center gap-2 bg-card border-b border-border px-4 py-2',
              'transition-[translate,visibility] duration-200 motion-reduce:transition-none',
              collapseControlRow
                ? 'invisible -translate-y-full'
                : 'visible translate-y-0'
            )}
          >
            {/* DV-2856: the expanded search field REPLACES the row's contents
                rather than stacking above it — the row's geometry has to stay
                constant for the collapse-on-scroll animation above. */}
            {searchExpanded && search ? (
              search
            ) : (
              <>
                {leadingActions}
                <div className="flex-1" />
                {photoSetSelect}
                {search}
                {kebab}
              </>
            )}
          </div>
        </>
      )}
    </TooltipProvider>
  );
};

export default AssetsControlBar;
